import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useStoreState } from '../../models/RootStore';
import { isEmpty } from 'lodash';
import { minsToTime, timeStringToMins } from '../../services/timeService';
import { isModbusPowerMeterFallback } from '../../screens/Control/ControlComponents/ModbusSchedule/constants';
import useScheduleControlPoints from './useScheduleControlPoints';
import { hasValue, checkRequiredFields, areAllScheduleActionsValid } from './validation';
const DEFAULT_POWER_ON_TIME = '00:00';
/**
 * Hook for managing Modbus schedule form state and validation. The action
 * list itself (which registers are on the schedule) lives in
 * useScheduleControlPoints; this hook owns the schedule's own fields
 * (name/active/days/time/tab) plus validation and submission.
 */
const useModbusScheduleForm = (
  modbusUnit,
  initialScheduleData = {}
) => {
  const editMode = !isEmpty(initialScheduleData);

  // Store state
  const timeFormat = useStoreState((state) => state.users?.timeFormat || '24');
  const types = useStoreState((state) => state.types || {});
  const { timeFormat: timeFormatTypes, scheduleCategories } = types;
  const { timeFormat: myTimeFormat } = useStoreState((state) => state.users?.me || {});

  // Form state
  const [scheduleName, setScheduleName] = useState('');
  const [scheduleDisabled, setScheduleDisabled] = useState(false);
  const [powerOnTime, setPowerOnTime] = useState(DEFAULT_POWER_ON_TIME);
  const [days, setDays] = useState([]);
  const [errorMsg, setErrorMsg] = useState('');
  const [dirty, setDirty] = useState(false);
  const [, setIsSubmitting] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  const [dates, setDates] = useState([]);
  const [tab, setTab] = useState(scheduleCategories.weekly);

  // Error message timeout ref
  const errorTimeoutRef = useRef(null);

  // Time format utilities
  const timeFormatObject = myTimeFormat
    ? timeFormatTypes[myTimeFormat]
    : timeFormatTypes[0];
  const is12Hours = timeFormatObject?.text === '12 hours';

  // Set error message with auto-reset
  const setErrorMsgWithTimeout = useCallback((msg) => {
    // Clear any existing timeout
    if (errorTimeoutRef.current) {
      clearTimeout(errorTimeoutRef.current);
    }

    // Set the error message
    setErrorMsg(msg);

    // Set timeout to clear the error
    if (msg) {
      errorTimeoutRef.current = setTimeout(() => {
        setErrorMsg('');
        errorTimeoutRef.current = null;
      }, 3500);
    }
  }, []);

  // A power-meter fallback unit (no uiTemplate, classified as a monitoring
  // entity) has nothing to schedule at all - its rows are readings, not
  // controls, even the ones that technically aren't flagged read-only.
  const isPowerMeterUnit = useMemo(() => isModbusPowerMeterFallback(modbusUnit), [modbusUnit]);

  const {
    registerMap,
    allRowsByKey,
    scheduleActions,
    setScheduleActions,
    scheduleActionsPayload,
    initializeFromModbusObjects,
    handleObjectValueChange,
    handleDeleteCustomRow,
    writableRegisterOptions,
    handleSelectCustomRow,
    handleNumericClick,
    activePickerId,
    activePickerRef,
    showSelectionMenu,
    openSelectionMenu,
    closeSelectionMenu,
  } = useScheduleControlPoints({ modbusUnit, isPowerMeterUnit, setDirty });

  // Initialize form with schedule data
  const initializeForm = useCallback(
    (scheduleData) => {
      const {
        name,
        powerOnTime: schedulePowerOnTime,
        days: scheduleDays = [],
        dates: scheduleDates = [],
        isDisabled,
        modbusObjects = [],
        scheduleCategory,
      } = scheduleData || {};

      setScheduleDisabled(isDisabled || false);
      setPowerOnTime(
        hasValue(schedulePowerOnTime)
          ? minsToTime(schedulePowerOnTime, timeFormat)
          : ''
      );
      setScheduleName(name || '');
      setDays(scheduleDays);
      const selectedDates = scheduleDates.map((date) => new Date(date));
      setDates(selectedDates);
      setTab(scheduleCategory);

      initializeFromModbusObjects(modbusObjects);
      setDirty(false);
      setErrorMsg('');
    },
    [hasValue, timeFormat, initializeFromModbusObjects]
  );

  // Reset form to initial state
  const resetForm = useCallback(() => {
    setScheduleName('');
    setScheduleDisabled(false);
    setPowerOnTime('');
    setDays([]);
    setDates([]);
    setTab(scheduleCategories.weekly);
    setScheduleActions({});
    setErrorMsg('');
    setDirty(false);
    setIsSubmitting(false);
    setIsInitialized(false);
  }, [setScheduleActions]);

  // Validate form data
  const validateForm = useCallback(() => {
    const requiredCheck = checkRequiredFields({
      scheduleName,
      tab,
      days,
      dates,
      powerOnTime,
      scheduleActionsPayload,
      scheduleCategories,
    });
    if (!requiredCheck.valid) {
      setErrorMsgWithTimeout(requiredCheck.error);
      return false;
    }

    const actionsCheck = areAllScheduleActionsValid(scheduleActions);
    if (!actionsCheck.valid) {
      setErrorMsgWithTimeout(actionsCheck.error);
      return false;
    }

    setErrorMsg('');
    return true;
  }, [
    scheduleName,
    powerOnTime,
    days,
    dates,
    tab,
    scheduleActionsPayload,
    scheduleActions,
    scheduleCategories,
    setErrorMsgWithTimeout,
  ]);

  // Prepare form data for submission
  const getFormData = useCallback(() => {
    if (!validateForm()) {
      return null;
    }

    const startHour = timeStringToMins(powerOnTime);

    const data = {
      isDisabled: scheduleDisabled,
      powerOnTime: startHour,
      name: scheduleName,
      modbusObjects: scheduleActionsPayload,
      scheduleCategory: tab,
    };
    if (tab === scheduleCategories.calendar) {
      data.dates = dates;
    }
    if (tab === scheduleCategories.weekly) {
      data.days = days;
    }
    return data;
  }, [
    validateForm,
    scheduleDisabled,
    powerOnTime,
    timeFormat,
    scheduleName,
    days,
    dates,
    tab,
    scheduleActionsPayload,
  ]);

  const handleNameChange = useCallback((name) => {
    setScheduleName(name);
    setDirty(true);
  }, []);

  const changeScheduleStatus = useCallback(() => {
    setScheduleDisabled((prev) => !prev);
    setDirty(true);
  }, []);

  const addRemoveDay = useCallback((newDays) => {
    setDays(newDays);
    setDirty(true);
  }, []);

  const changeDates = useCallback((newDates) => {
    setDates(newDates);
    setDirty(true);
  }, []);

  const changeTab = useCallback((newTab) => {
    setTab(newTab);
    setDirty(true);
  }, []);

  const onSetTime = useCallback((time) => {
    setPowerOnTime(time);
    setDirty(true);
  }, []);

  // Initialize the form once the unit's rows are loaded in edit mode.
  // Don't require registerMap to be non-empty: a unit may have no editable
  // points but still have existing schedule objects that need to be resolved.
  useEffect(() => {
    if (
      editMode &&
      initialScheduleData &&
      Object.keys(allRowsByKey).length > 0 &&
      !isInitialized
    ) {
      initializeForm(initialScheduleData);
      setIsInitialized(true);
    }
  }, [
    editMode,
    initialScheduleData,
    registerMap,
    allRowsByKey,
    initializeForm,
    isInitialized,
  ]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (errorTimeoutRef.current) {
        clearTimeout(errorTimeoutRef.current);
      }
    };
  }, []);

  return {
    scheduleName,
    handleNameChange,
    scheduleDisabled,
    changeScheduleStatus,
    powerOnTime,
    onSetTime,
    days,
    addRemoveDay,
    dates,
    changeDates,
    tab,
    changeTab,

    scheduleActions,
    registerMap,
    isPowerMeterUnit,
    dirty,
    errorMsg,
    is12Hours,

    setIsSubmitting,
    setErrorMsg,

    handleObjectValueChange,
    handleDeleteCustomRow,
    writableRegisterOptions,
    handleSelectCustomRow,
    handleNumericClick,
    activePickerId,
    activePickerRef,
    showSelectionMenu,
    openSelectionMenu,
    closeSelectionMenu,

    resetForm,
    validateForm,
    getFormData,
    setDirty,
  };
};

export default useModbusScheduleForm;
